import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Chart } from 'chart.js';
import { createVerticalBarChart } from './util/VerticalBarChart';
import {
    getChartDisplayConfig,
} from '@/features/charts/useBarChartScrollContainer';
import { useSmUpViewport } from '@/hooks/useSmUpViewport';
import type { BarChartViewMode } from '@/features/charts/BarChartViewModeToggle';
import { BarChartScrollContainer } from '@/features/charts/BarChartScrollContainer';
import BarChartIcon from '../../../assets/images/sidebar/analysis_icon.png';
import { TransitionEventItems } from '@/types/AnalysisResultType';
import { removeYearFromDate, removeZeroPaddingFromDate, getWeekday } from '@/utils/time';
import { useTranslation } from 'react-i18next';
import { getHoursAndMinutes } from '@/utils/time';

type NormalVerticalBarChartInnerProps = {
    data: TransitionEventItems[];
    previousData?: TransitionEventItems[];
    chartBarLabelLayout: 'fit' | 'expand';
    innerWidthPx: number;
    useMonthAwarePreviousDay: boolean;
};

const NormalVerticalBarChartInner = ({
    data,
    previousData,
    chartBarLabelLayout,
    innerWidthPx,
    useMonthAwarePreviousDay: usePrevDayForMonthAware,
}: NormalVerticalBarChartInnerProps) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const chartRef = useRef<Chart<'bar' | 'line'> | null>(null);
    const { t, i18n } = useTranslation('analysis');

    const monthAwareLabel = useCallback(
        (currentDate: string, previousDate?: string) => {
            if (!currentDate) return '';
            const curNorm = currentDate.includes('/') ? currentDate.replace(/\//g, '-') : currentDate;
            const prevNorm = previousDate
                ? previousDate.includes('/')
                    ? previousDate.replace(/\//g, '-')
                    : previousDate
                : undefined;
            const curMonth = curNorm.split('-')[1];
            const prevMonth = prevNorm?.split('-')[1];
            const trimmed = removeZeroPaddingFromDate(removeYearFromDate(curNorm));
            const [m, d] = trimmed.split('-');
            const base = !prevMonth || prevMonth !== curMonth ? `${m}/${d}` : d;
            const weekday = getWeekday(curNorm, i18n.language);
            if (!weekday) return base.replace(/-/g, '/');
            const isJa = i18n.language?.startsWith('ja');
            const shortW = isJa ? weekday.slice(0, 1) : weekday.slice(0, 2);
            const display = base.replace(/-/g, '/');
            return `${display}(${shortW})`;
        },
        [i18n.language],
    );

    const formatDuration = useCallback((minutes: number) => {
        const [hours, mins] = getHoursAndMinutes(minutes);
        if (hours === 0) {
            return `${mins}m`;
        }
        if (mins === 0) {
            return `${hours}h`;
        }
        return `${hours}h${mins}m`;
    }, []);

    const averageAllLabel = useMemo(() => t('chart.tooltip.averageAll'), [t]);
    const averageActiveLabel = useMemo(() => t('chart.tooltip.averageActive'), [t]);
    const previousLabel = useMemo(() => t('chart.tooltip.previous'), [t]);

    useEffect(() => {
        if (data.length === 0) return;
        if (!canvasRef.current) return;

        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null;
        }

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;

        const labels = data.map((item, idx) =>
            monthAwareLabel(
                item.date,
                usePrevDayForMonthAware && idx > 0 ? data[idx - 1].date : undefined,
            ),
        );

        const prevLabels = previousData?.map((item, idx, arr) =>
            monthAwareLabel(
                item.date,
                usePrevDayForMonthAware && idx > 0 ? arr[idx - 1].date : undefined,
            ),
        );

        const currentVals = data.map((d) => d.time);

        let prevVals: number[] | undefined = undefined;
        if (previousData && previousData.length) {
            prevVals = data.map((_, idx) =>
                idx < previousData.length ? previousData[idx].time : 0,
            );
        }

        chartRef.current =
            createVerticalBarChart(ctx, labels, currentVals, prevLabels, prevVals, {
                averageAllLabel,
                averageActiveLabel,
                previousLabel,
                formatDuration,
                formatShortDuration: formatDuration,
                barLabelLayout: chartBarLabelLayout,
            }) ?? null;

        return () => {
            chartRef.current?.destroy();
        };
    }, [
        data,
        previousData,
        averageAllLabel,
        averageActiveLabel,
        previousLabel,
        formatDuration,
        monthAwareLabel,
        chartBarLabelLayout,
        usePrevDayForMonthAware,
    ]);

    useEffect(() => {
        chartRef.current?.resize();
    }, [innerWidthPx, chartBarLabelLayout]);

    return <canvas ref={canvasRef} className="block h-full w-full max-w-none" />;
};

type NormalVerticalBarChartProps = {
    data: TransitionEventItems[];
    previousData?: TransitionEventItems[];
    barViewMode?: BarChartViewMode;
};

/**
 * バーグラフコンポーネント
 */
const NormalVerticalBarChart = ({ data, previousData, barViewMode: barViewModeProp }: NormalVerticalBarChartProps) => {
    const { t } = useTranslation('analysis');
    const isSmUpViewport = useSmUpViewport();
    const labelCount = data.length;
    const viewMode = barViewModeProp ?? 'fit';
    
    const config = getChartDisplayConfig('transition', labelCount, isSmUpViewport, viewMode);
    const { scrollLayout, showBarLabels, useMonthAwareLabels } = config;
    const chartBarLabelLayout = showBarLabels ? 'expand' : 'fit';

    if (data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full">
                <img src={BarChartIcon} alt={t('chart.noData')} className="w-20 h-20" />
                <p className="text-xl mt-4">{t('chart.noData')}</p>
            </div>
        );
    }

    return (
        <BarChartScrollContainer labelCount={labelCount} scrollLayout={scrollLayout}>
            {({ innerWidthPx }) => (
                <NormalVerticalBarChartInner
                    data={data}
                    previousData={previousData}
                    chartBarLabelLayout={chartBarLabelLayout}
                    innerWidthPx={innerWidthPx}
                    useMonthAwarePreviousDay={useMonthAwareLabels}
                />
            )}
        </BarChartScrollContainer>
    );
};

export default NormalVerticalBarChart;
